import React, { useEffect, useMemo, useState } from "react";


import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getStoredUser } from "@/lib/auth";
import { usePermissions } from "@/hooks/usePermissions";
import {
  bookingAgentOptionsFrom,
  salesAgentOptionsFrom,
} from "@/lib/appointmentActors";
import MultiSelectFilter from "@/components/leads/MultiSelectFilter";
import SingleSelectFilter from "@/components/leads/SingleSelectFilter";
import SetAppointmentLeadDetailsCard from "@/components/appointments/SetAppointmentLeadDetailsCard";



function newBookingOperationId() {
  // Matches the existing MissionAssist convention (builderDraft.js,
  // LandingPageBuilder.jsx) -- browser-native, no new dependency. Falls back
  // where `crypto.randomUUID` is unavailable (this repo's jsdom test
  // environment, and non-HTTPS browser contexts). The backend accepts any
  // bounded [A-Za-z0-9_.:-] string of 8-128 chars, so the fallback is a
  // first-class value here rather than a degraded one.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `bk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Render a stored UTC instant as the value an `<input type="datetime-local">`
 * expects: `YYYY-MM-DDTHH:mm` of BROWSER-LOCAL wall-clock, with no zone.
 *
 * It must be local, and it must be built from the local getters. The tempting
 * `new Date(iso).toISOString().slice(0, 16)` expresses UTC wall-clock, and
 * since submitting does `new Date(value)` -- which reads a naive string as
 * LOCAL -- that form silently moves the Appointment by the browser's offset
 * the moment anyone outside UTC opens the dialog and saves without touching
 * the field. This is the exact inverse of the submit path, so an untouched
 * reschedule re-encodes to the instant it started from.
 */
export function toDateTimeLocalValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * How long the Appointment lasts, in ms, or null when it does not say.
 *
 * Only a genuine positive interval counts. A missing, unparseable or
 * non-positive one returns null so the caller can omit `end_at` entirely and
 * leave the stored value exactly as the backend already has it -- inventing a
 * duration here would write a number nobody chose.
 */
export function appointmentDurationMs(appointment) {
  const start = new Date(appointment?.start_at ?? NaN).getTime();
  const end = new Date(appointment?.end_at ?? NaN).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  const duration = end - start;
  return duration > 0 ? duration : null;
}

export default function SetAppointmentDialog({ lead, open, onOpenChange, onConverted, mode = "convert", appointmentId = null, appointment = null }) {
  const { toast } = useToast();
  const user = getStoredUser();
  // P4: canonical selected-Account role. `user` above is the global login
  // principal and is used ONLY for identity (id/name/email), never authority.
  const { userRole } = usePermissions();

  const [busy, setBusy] = useState(false);

  // One booking operation = one open dialog. Minted OUTSIDE the submit
  // handler on purpose: submitting can fail and the dialog stays open
  // inviting "Please try again", so a token minted per submit would be new
  // on every retry and would make the backend fence useless. Reopening the
  // dialog, or a successful booking, deliberately starts a new operation.
  const [bookingOperationId, setBookingOperationId] = useState(() => newBookingOperationId());

  // A follow-up is a DIFFERENT operation from a lead conversion, so it carries
  // its own token and never reuses the convert token's value. Same lifecycle
  // rule: minted outside submit, stable across retries while this dialog stays
  // open, new on close/reopen and after a success.
  const [followUpOperationId, setFollowUpOperationId] = useState(() => newBookingOperationId());
  const [teamUsers, setTeamUsers] = useState([]);

  const [selectedSalesAgentId, setSelectedSalesAgentId] = useState("");
  const [selectedBookingAgentId, setSelectedBookingAgentId] = useState("");

  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [manualStartAt, setManualStartAt] = useState("");
  const [timezoneVerified, setTimezoneVerified] = useState(false);

  const manualCalendar = useMemo(
    () => ({ id: "missionassist", title: "MissionAssist Calendar", booking_link: null, is_manual: true }),
    []
  );
  const mergedCalendars = useMemo(() => [manualCalendar, ...(calendars || [])], [calendars, manualCalendar]);

  // Rescheduling an Appointment that already exists, as opposed to booking a
  // new one.
  const isExistingReschedule = mode === "schedule" && Boolean(appointment);

  /**
   * Operations that carry an existing Appointment's actors forward rather than
   * assigning new ones.
   *
   * Rescheduling omits the actor fields entirely and the backend preserves
   * what is stored. Creating a follow-up sends them as null and the backend
   * inherits each one independently from the parent -- sales and booking
   * resolved separately, with no cross-fallback, and only the ones the parent
   * actually has.
   *
   * Neither operation assigns an actor, so neither may demand one. Naming the
   * property they share, rather than listing modes at each site, is what keeps
   * the selectors, the requirement and the self-default from disagreeing.
   */
  const operationInheritsActors = isExistingReschedule || mode === "follow_up";

  // Rescheduling starts from the time the Appointment already has; booking a
  // new one or a follow-up starts blank, because there is no existing time to
  // show and inheriting one would quietly propose a slot nobody picked.
  //
  // This is computed and applied INSIDE the open/reset effect rather than in
  // an effect of its own: that effect unconditionally blanks manualStartAt, so
  // a separate initializer would race it and be overwritten on whichever
  // ordering React chose.
  const initialStartAt =
    mode === "schedule" ? toDateTimeLocalValue(appointment?.start_at) : "";

  // Rescheduling an existing Appointment goes through the MissionAssist
  // manual date/time path -- `PUT /appointments/{id}` -- so that calendar is
  // chosen up front rather than left to the generic defaulting below.
  //
  // That defaulting picks MissionAssist only when /calendars comes back EMPTY
  // or fails. It was written for booking a new Appointment, where offering
  // the account's real calendars is the point. Borrowed by reschedule it
  // meant that an account with any calendar at all -- Preview has "MP -
  // Initial" and "MP - Follow Up" -- opened the dialog with nothing selected,
  // so the date/time panel never rendered and the Appointment could not be
  // rescheduled at all. The correct calendar for this operation does not
  // depend on how many other calendars happen to exist, so it is no longer
  // inferred from them.
  //
  // Deliberately NOT keyed off appointment.calendar_id: that field is written
  // only by the GHL webhook and conversation funnels, never by manual
  // booking, Path 1B convert or the follow-up writer, so for the Appointments
  // this operation acts on it is absent.
  const initialCalendar = isExistingReschedule ? manualCalendar : null;

  useEffect(() => {
    if (!open) return;

    console.log('[SetAppointmentDialog] Dialog opened, fetching calendars...');
    
    setCalendars([]);
    setSelectedCalendar(initialCalendar);
    setManualStartAt(initialStartAt);
    setTimezoneVerified(false);
    setBookingOperationId(newBookingOperationId());
    setFollowUpOperationId(newBookingOperationId());

    const load = async () => {
      try {
        console.log('[SetAppointmentDialog] Calling /api/calendars...');
        const res = await api.get("/calendars");
        const items = res.data || [];
        console.log('[SetAppointmentDialog] Received calendars:', items);
        setCalendars(items);
        // Note this can only ever SELECT manualCalendar, never deselect, so a
        // non-empty response cannot undo the reschedule choice made above.
        if (items.length === 0) {
          console.log('[SetAppointmentDialog] No calendars found, selecting manual calendar');
          setSelectedCalendar(manualCalendar);
        }
      } catch (e) {
        console.error('[SetAppointmentDialog] Failed to load calendars:', e);
        setCalendars([]);
        setSelectedCalendar(manualCalendar);
      }
    };

    load();
  }, [open, manualCalendar, initialStartAt, initialCalendar]);

  useEffect(() => {
    if (!open) return;

    const loadUsers = async () => {
      try {
        const res = await api.get("/team/users");
        setTeamUsers(res.data?.items || []);
      } catch {
        setTeamUsers([]);
      }
    };

    loadUsers();
  }, [open]);

  const leadNotBooked = String(lead?.status || "").toLowerCase() !== "booked";

  // P4: the CURRENT user's role in the SELECTED Account.
  //
  // This read `user?.role` -- the global `users.role` off the stored login
  // principal -- which has not been an Account-role authority source since
  // WP-1 and is wrong outright for anyone holding different roles in
  // different Accounts. `usePermissions().userRole` is the canonical shared
  // effective-role projection for the selected Account, the same one the rest
  // of the permission surface reads.
  //
  // Vocabulary is canonical too: "agent" was the pre-P4 name for
  // `sales_agent`, and "super_admin" is platform authority that is never an
  // Account role.
  const currentAccountRole = userRole || null;
  const isAdminRole = currentAccountRole === "owner" || currentAccountRole === "admin";
  const isBookingAgentRole = currentAccountRole === "booking_agent";
  const isSalesAgentRole = currentAccountRole === "sales_agent";

  const salesAgentOptions = useMemo(() => salesAgentOptionsFrom(teamUsers), [teamUsers]);

  // P4: the two current-user fallbacks that used to be unshifted here are
  // REMOVED, not replaced.
  //
  // Being a Sales Agent, or an admin listed as one, made the current user a
  // selectable Booking Agent -- which collapses Activity Agent, Booking Agent
  // and Originator into one identity for the convenience of populating a
  // dropdown. Booking-Agent eligibility is the `booking_agent` membership
  // role and nothing else. If this Account has no active `booking_agent`
  // membership, this list is legitimately empty.
  const bookingAgentOptions = useMemo(() => bookingAgentOptionsFrom(teamUsers), [teamUsers]);

  const selfIsSalesAgentOption = useMemo(
    () => salesAgentOptions.some((u) => u.id === user?.id),
    [salesAgentOptions, user?.id]
  );
  const selfIsBookingAgentOption = useMemo(
    () => bookingAgentOptions.some((u) => u.id === user?.id),
    [bookingAgentOptions, user?.id]
  );

  // Actor selection gates operations that ASSIGN actors -- and only those.
  //
  // Gating on `leadNotBooked` alone made the wrong thing reachable in ordinary
  // use: the Appointment GET serves the LIVE Lead as lead_snapshot, and
  // completing a summary with customer_status "Sold" sets the Lead to Sold.
  // Every Appointment on a converted Lead then opened demanding two agents --
  // blank fields, never initialized from the Appointment, that the operation
  // does not even send -- with Done disabled until they were chosen.
  //
  // #629 exempted reschedule. Follow-up satisfies the same premise and is now
  // the common path, because a Completed outcome is follow-up eligible: close
  // an Appointment as Sold, answer Yes, and the follow-up dialog was blocked.
  //
  // `leadNotBooked` itself is unchanged; only these two predicates narrow.
  const showSalesAgentDropdown =
    !operationInheritsActors && leadNotBooked && (isAdminRole || isBookingAgentRole);
  const showBookingAgentDropdown =
    !operationInheritsActors && leadNotBooked && (isAdminRole || isSalesAgentRole);

  useEffect(() => {
    if (!open) return;

    // Hiding the selectors is not sufficient on its own. The follow-up payload
    // reads `selectedSalesAgentId` directly, not the selector's visibility, so
    // a self-default left in state would be transmitted as an explicit actor
    // and OVERRIDE the parent's -- silently reassigning A's Sales Agent to
    // whoever happened to schedule B. Cleared here so an inheriting operation
    // sends null and the backend's inheritance is what decides.
    if (operationInheritsActors || !leadNotBooked) {
      setSelectedSalesAgentId("");
      setSelectedBookingAgentId("");
      return;
    }

    // P4: convenience pre-selection may only choose an actor who is
    // canonically eligible for THAT role. It used to select the current user
    // as Booking Agent because they were an admin listed as a Sales Agent, or
    // because they were a Sales Agent -- writing a booking_agent_user_id for
    // someone with no `booking_agent` membership. Pre-selecting from the
    // canonical option sets removes that without adding a replacement rule:
    // when the caller is not an eligible actor, the field simply stays empty.
    setSelectedSalesAgentId(selfIsSalesAgentOption ? user?.id || "" : "");
    setSelectedBookingAgentId(selfIsBookingAgentOption ? user?.id || "" : "");
  }, [open, operationInheritsActors, leadNotBooked, selfIsSalesAgentOption, selfIsBookingAgentOption, user?.id]);

  useEffect(() => {
    setTimezoneVerified(false);
  }, [selectedCalendar?.id]);

  const requireAgentSelection = leadNotBooked && (showSalesAgentDropdown || showBookingAgentDropdown);
  const missingAgentSelection =
    (showSalesAgentDropdown && !selectedSalesAgentId) || (showBookingAgentDropdown && !selectedBookingAgentId);

  const leadRows = useMemo(() => {
    const nameParts = String(lead?.full_name || "").trim().split(/\s+/).filter(Boolean);
    const firstName = lead?.first_name || nameParts[0] || "—";
    const lastName = lead?.last_name || (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "—");
    const phoneNumber = lead?.phone || lead?.phone_number || "—";
    const email = lead?.email || "—";

    return [
      { label: "First Name", value: firstName, testId: "first-name" },
      { label: "Last Name", value: lastName, testId: "last-name" },
      { label: "Phone", value: phoneNumber, testId: "phone" },
      { label: "Email", value: email, testId: "email" },
      { label: "MissionAssist Lead ID", value: lead?.id || "—", testId: "lead-id" },
    ];
  }, [lead?.email, lead?.first_name, lead?.full_name, lead?.id, lead?.last_name, lead?.phone, lead?.phone_number]);

  const handleCopy = async (value, label) => {
    const text = String(value || "").trim();
    if (!text || text === "—") {
      toast({ title: `No ${label} to copy`, variant: "destructive" });
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${label} copied` });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const isManual = selectedCalendar?.id === manualCalendar.id;
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // A follow-up always needs an explicit time: it is a new booking, not an
  // external-calendar placeholder.
  const requiresStartAt =
    mode === "follow_up" || (isManual && (mode === "convert" || (mode === "schedule" && isManual)));
  const externalConfirmRequired = Boolean(selectedCalendar && !isManual);
  const confirmDisabled =
    busy ||
    !selectedCalendar ||
    (requiresStartAt && !manualStartAt) ||
    (externalConfirmRequired && !timezoneVerified) ||
    (requireAgentSelection && missingAgentSelection);

  const onConvert = async () => {
    if (!lead?.id) {
      toast({ title: "Missing lead ID", variant: "destructive" });
      return;
    }
    if (isManual && !manualStartAt) {
      toast({ title: "Select a date/time", variant: "destructive" });
      return;
    }
    if (!isManual && !timezoneVerified) {
      toast({
        title: "Confirm scheduling",
        description: "Please verify the timezone is correct.",
        variant: "destructive",
      });
      return;
    }
    if (requireAgentSelection && missingAgentSelection) {
      toast({
        title: "Select agents",
        description: "Please select the required agent(s) before booking.",
        variant: "destructive",
      });
      return;
    }

    setBusy(true);
    try {
      if (mode === "follow_up") {
        if (!appointmentId) {
          toast({ title: "Missing appointment", variant: "destructive" });
          return;
        }
        const dt = new Date(manualStartAt);
        if (Number.isNaN(dt.getTime())) {
          toast({ title: "Invalid date/time", variant: "destructive" });
          return;
        }
        const fuSalesAgent = (teamUsers || []).find((u) => u.id === selectedSalesAgentId);
        const fuBookingAgent = (teamUsers || []).find((u) => u.id === selectedBookingAgentId);
        const created = await api.post(`/appointments/${appointmentId}/follow-up`, {
          booking_operation_id: followUpOperationId,
          start_at: dt.toISOString(),
          timezone: localTimezone,
          sales_agent_user_id: fuSalesAgent?.id || null,
          sales_agent_name: fuSalesAgent?.name || fuSalesAgent?.email || null,
          booking_agent_user_id: fuBookingAgent?.id || null,
          booking_agent_name: fuBookingAgent?.name || fuBookingAgent?.email || null,
        });
        setFollowUpOperationId(newBookingOperationId());
        toast({ title: "Appointment booked" });
        onConverted?.(created?.data?.id || null);
        onOpenChange(false);
        return;
      }

      if (mode === "schedule" && isManual) {
        if (!appointmentId) {
          toast({ title: "Missing appointment", variant: "destructive" });
          return;
        }
        const dt = new Date(manualStartAt);
        if (Number.isNaN(dt.getTime())) {
          toast({ title: "Invalid date/time", variant: "destructive" });
          return;
        }
        const iso = dt.toISOString();

        // Moving start_at alone leaves the stored end_at where it was, so a
        // reschedule to a later slot used to leave the Appointment ending
        // before it begins. Carry the ORIGINAL duration across rather than
        // recomputing one: a 30- or 90-minute Appointment must not silently
        // become an hour. When the Appointment has no usable interval, end_at
        // is omitted and the backend's patch leaves it untouched.
        const durationMs = appointmentDurationMs(appointment);
        const patch = {
          start_at: iso,
          status: "Scheduled",
          timezone: localTimezone,
        };
        if (durationMs !== null) {
          patch.end_at = new Date(dt.getTime() + durationMs).toISOString();
        }

        await api.put(`/appointments/${appointmentId}`, patch);
        toast({ title: "Appointment scheduled" });
        onConverted?.();
        onOpenChange(false);
        return;
      }

      if (mode === "convert") {
        const salesAgent = (teamUsers || []).find((u) => u.id === selectedSalesAgentId);
        const bookingAgent = (teamUsers || []).find((u) => u.id === selectedBookingAgentId);

        if (!isManual) {
          // External calendar: create an internal appointment now so agent fields appear on Appointment Details.
          await api.post(`/leads/${lead.id}/convert`, {
            calendar_title: selectedCalendar?.title || null,
            booking_link: selectedCalendar?.booking_link || null,
            timezone: localTimezone,
            start_at: null,
            end_at: null,
            sales_agent_user_id: showSalesAgentDropdown ? (salesAgent?.id || null) : (isSalesAgentRole ? (user?.id || null) : null),
            sales_agent_name: showSalesAgentDropdown ? (salesAgent?.name || salesAgent?.email || null) : (isSalesAgentRole ? (user?.name || user?.email || null) : null),
            booking_agent_user_id: showBookingAgentDropdown ? (bookingAgent?.id || null) : (isBookingAgentRole ? (user?.id || null) : null),
            booking_agent_name: showBookingAgentDropdown ? (bookingAgent?.name || bookingAgent?.email || null) : (isBookingAgentRole ? (user?.name || user?.email || null) : null),
          });

          toast({ title: "Appointment created and lead marked as booked" });
          onConverted?.();
          onOpenChange(false);
          return;
        }

        const payload = {
          calendar_title: selectedCalendar?.title || null,
          booking_link: selectedCalendar?.booking_link || null,

          sales_agent_user_id: showSalesAgentDropdown ? (salesAgent?.id || null) : (isSalesAgentRole ? (user?.id || null) : null),
          sales_agent_name: showSalesAgentDropdown ? (salesAgent?.name || salesAgent?.email || null) : (isSalesAgentRole ? (user?.name || user?.email || null) : null),

          booking_agent_user_id: showBookingAgentDropdown ? (bookingAgent?.id || null) : (isBookingAgentRole ? (user?.id || null) : null),
          booking_agent_name: showBookingAgentDropdown ? (bookingAgent?.name || bookingAgent?.email || null) : (isBookingAgentRole ? (user?.name || user?.email || null) : null),
        };
        if (isManual && manualStartAt) {
          const dt = new Date(manualStartAt);
          if (Number.isNaN(dt.getTime())) {
            toast({ title: "Invalid date/time", variant: "destructive" });
            return;
          }
          payload.start_at = dt.toISOString();
          payload.timezone = localTimezone;
        }
        // Path 1B only. The three Path 1a callers are deliberately untouched.
        await api.post(`/leads/${lead.id}/convert`, { ...payload, booking_operation_id: bookingOperationId });
        setBookingOperationId(newBookingOperationId());
        toast({
          title: "Booked",
          description: "Appointment created and lead marked as booked.",
        });
        onConverted?.();
      }

      onOpenChange(false);
    } catch (e) {
      toast({ title: "Could not convert", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        data-testid="set-appointment-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="set-appointment-title">Set Appointment</DialogTitle>
        </DialogHeader>

        <div
          className="grid flex-1 grid-cols-1 gap-4 overflow-y-auto lg:grid-cols-[360px_1fr] lg:items-stretch"
          data-testid="set-appointment-layout"
        >
          <div className="space-y-4" data-testid="set-appointment-left-col">
            {showSalesAgentDropdown || showBookingAgentDropdown ? (
              <div className="space-y-3" data-testid="set-appointment-agents-section">
                {showSalesAgentDropdown ? (
                  <div data-testid="set-appointment-sales-agent-section">
                    <div className="text-xs text-slate-200/70" data-testid="set-appointment-sales-agent-label">Sales Agent</div>
                    <div className="mt-2">
                      <SingleSelectFilter
                        placeholder="Select sales agent"
                        value={selectedSalesAgentId}
                        onChange={(v) => setSelectedSalesAgentId(v)}
                        options={salesAgentOptions.map((u) => ({ value: u.id, label: u.name || u.email }))}
                        testIdPrefix="set-appointment-sales-agent"
                      />
                    </div>
                  </div>
                ) : null}

                {showBookingAgentDropdown ? (
                  <div data-testid="set-appointment-booking-agent-section">
                    <div className="text-xs text-slate-200/70" data-testid="set-appointment-booking-agent-label">Booking Agent</div>
                    <div className="mt-2">
                      <SingleSelectFilter
                        placeholder="Select booking agent"
                        value={selectedBookingAgentId}
                        onChange={(v) => setSelectedBookingAgentId(v)}
                        options={bookingAgentOptions.map((u) => ({ value: u.id, label: u.name || u.email }))}
                        testIdPrefix="set-appointment-booking-agent"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div data-testid="set-appointment-calendars-section">
              <div className="text-xs text-slate-200/70" data-testid="set-appointment-calendars-label">Calendars</div>
              <div className="mt-2">
                {mergedCalendars.length === 0 ? (
                  <div className="rounded-2xl bg-white/5 p-3 text-sm text-slate-200/70 ring-1 ring-white/10" data-testid="set-appointment-calendars-empty">
                    No calendars yet. Add one in Settings.
                  </div>
                ) : (
                  <SingleSelectFilter
                    placeholder="Select a calendar"
                    value={selectedCalendar?.id || ""}
                    onChange={(id) => {
                      const found = (mergedCalendars || []).find((c) => c.id === id) || null;
                      setSelectedCalendar(found);
                      setManualStartAt("");
                    }}
                    options={mergedCalendars.map((c) => ({ value: c.id, label: c.title }))}
                    testIdPrefix="set-appointment-calendars"
                  />
                )}
              </div>
            </div>

            <SetAppointmentLeadDetailsCard lead={lead} rows={leadRows} onCopy={handleCopy} />
          </div>

          <div className="flex min-h-0 flex-col" data-testid="set-appointment-right-col">
            {selectedCalendar ? (
              isManual ? (
                <div className="flex min-h-[520px] flex-1 flex-col items-center justify-start rounded-2xl bg-black/10 px-6 pt-16 text-sm text-slate-200/70 ring-1 ring-white/10" data-testid="set-appointment-manual-panel">
                  <div className="text-sm text-slate-100">Set Appointment Date & Time</div>
                  <div className="mt-4 w-full max-w-sm">
                    <Input
                      type="datetime-local"
                      value={manualStartAt}
                      onChange={(e) => setManualStartAt(e.target.value)}
                      className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                      style={{ colorScheme: "dark" }}
                      data-testid="set-appointment-manual-datetime"
                    />
                  </div>
                  <div className="mt-3 text-xs text-slate-200/60">Time is saved in your local timezone ({localTimezone}).</div>
                </div>
              ) : (
                <div className="flex min-h-[520px] h-full flex-1 flex-col min-h-0" data-testid="set-appointment-external-panel">
                  <div className="flex h-full flex-1 overflow-hidden rounded-2xl ring-1 ring-white/10" data-testid="set-appointment-calendar-iframe">
                    {/* P0 UX: Scale iframe to 85% for better calendar readability */}
                    <div className="h-full w-full overflow-hidden" style={{ position: 'relative' }}>
                      <iframe 
                        title="booking" 
                        src={selectedCalendar.booking_link} 
                        className="bg-white"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '117.65%',  /* 100% / 0.85 to compensate for scale */
                          height: '117.65%',
                          transform: 'scale(0.85)',
                          transformOrigin: 'top left',
                          border: 'none',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="flex min-h-[520px] flex-1 items-start justify-center rounded-2xl bg-black/10 pt-16 text-sm text-slate-200/70 ring-1 ring-white/10" data-testid="set-appointment-calendar-placeholder">
                Select a calendar to book.
              </div>
            )}
          </div>
        </div>

        <DialogFooter
          className="shrink-0 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4 sm:space-x-0"
        >
          <div className="hidden lg:block w-[360px] shrink-0" aria-hidden="true" />

          <div className="flex flex-1 items-center justify-between gap-4">
            {externalConfirmRequired ? (
              <div className="flex h-9 items-center gap-2" data-testid="set-appointment-footer-timezone">
                <Checkbox
                  checked={timezoneVerified}
                  onCheckedChange={(value) => setTimezoneVerified(Boolean(value))}
                  className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                  data-testid="set-appointment-timezone-confirm-checkbox"
                />
                <span className="text-sm text-slate-200/80" data-testid="set-appointment-timezone-confirm-label">
                  Verified Time Zone is correct
                </span>
              </div>
            ) : (
              <div className="h-9" />
            )}

            <div className="flex items-center justify-end gap-2">
              <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              data-testid="set-appointment-close"
            >
              Close
            </Button>
            {mode === "convert" ? (
              <Button
                onClick={onConvert}
                disabled={confirmDisabled}
                data-testid="set-appointment-convert"
              >
                {busy ? "Booking…" : "Booked"}
              </Button>
            ) : (
              <Button
                onClick={onConvert}
                disabled={confirmDisabled}
                data-testid="set-appointment-done"
              >
                Done
              </Button>
            )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
